/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { skills } from '../data/portfolio';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';

import { Skill } from '../types';
import './Skills.css';

// Stockage global des animations déjà jouées (persiste même si le composant est démonté)
const animatedSkills = new Set<string>();

const Skills: React.FC = () => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Catégories de compétences
  const skillCategories = skills.reduce((acc, skill) => {
    const { category } = skill;
    if (!acc[category]) {
      acc[category] = [];
    }
    (acc[category] as Skill[]).push(skill);
    return acc;
  }, {} as Record<string, Skill[]>);

  // Tri des compétences par niveau pour chaque catégorie
  for (const category in skillCategories) {
    skillCategories[category].sort((a, b) => b.level - a.level);
  }

  const getLevelColor = (level: number) => {
    if (level < 33) return '#52f85aff';
    if (level <= 66) return '#5255f3ff';
    return '	#4CAF50';
  };

  const SkillCard: React.FC<{ skill: Skill; index: number }> = ({ skill, index }) => {
    const ref = useRef(null);
    const isAlreadyAnimated = animatedSkills.has(skill.name);
    const [hasAnimated, setHasAnimated] = useState(isAlreadyAnimated);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    useEffect(() => {
      if (isInView && !hasAnimated) {
        animatedSkills.add(skill.name);
        setHasAnimated(true);
      }
    }, [isInView, hasAnimated]);

    const finalColor = getLevelColor(skill.level);
    const rainbowColors = ['#FF0000', '#FF7F00', '#FFFF00', '#00FF00', '#0000FF', '#4B0082', '#9400D3'];
    // Création d'un cycle de couleurs qui se termine par la bonne couleur
    const animationColors = [...rainbowColors, ...[...rainbowColors].reverse(), finalColor];

    return (
      <motion.div
        ref={ref}
        className="skill-card"
        initial={isAlreadyAnimated ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        animate={isInView || isAlreadyAnimated ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.6, delay: isAlreadyAnimated ? 0 : index * 0.1, ease: "easeOut" }}
      >
        <div className="skill-icon">
          {typeof skill.icon === 'string' ? (
            skill.icon.startsWith('fab') || skill.icon.startsWith('fas') ? (
              <i className={skill.icon}></i>
            ) : (
              <img src={skill.icon} alt={skill.name} />
            )
          ) : (
            <skill.icon />
          )}
        </div>
        <h4 className="skill-name">{skill.name}</h4>
        <div className="skill-level">
          <motion.div
            className="skill-level-indicator"
            initial={isAlreadyAnimated ? { backgroundColor: finalColor } : { backgroundColor: rainbowColors[0] }}
            animate={hasAnimated && !isAlreadyAnimated ? {
              rotateY: [0, 360, 720, 1080],
              backgroundColor: animationColors,
            } : { backgroundColor: finalColor, rotateY: 0 }}
            transition={hasAnimated && !isAlreadyAnimated ? {
              rotateY: { duration: 3, ease: "easeOut" },
              backgroundColor: { duration: 3, ease: "linear" }
            } : { duration: 0.5 }}
          />
        </div>
      </motion.div>
    );
  };

  const MoreButton: React.FC<{
    className: string;
    onClick: () => void;
    index: number;
    children: React.ReactNode;
  }> = ({ className, onClick, index, children }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
      <motion.div
        ref={ref}
        className={className}
        onClick={onClick}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
      >
        {children}
      </motion.div>
    );
  };

  return (
    <section id="skills" className="skills section-padding">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">
            Mes Compétences
          </h2>
          <div className="skill-legend">
            <div className="legend-header">
              <TipsAndUpdatesIcon htmlColor="#fbc02d" />
              <h3>Code Couleurs des Compétences</h3>
            </div>
            <ul className="legend-list">
              <li><span className="legend-dot" style={{ backgroundColor: getLevelColor(99) }}></span> J'utilise au quotidien.</li>
              <li><span className="legend-dot" style={{ backgroundColor: getLevelColor(50) }}></span> J'utilise avec la  documentation à portée de main. </li>
              <li><span className="legend-dot" style={{ backgroundColor: getLevelColor(30) }}></span> Je peux exploiter, connaissances de base. </li>
            </ul>
          </div>


        </div>

        <div className="skills-content">
          <div className="skills-grid">
            {Object.entries(skillCategories).map(([category, categorySkills]) => {
              const isExpanded = expandedCategories.has(category);
              const maxVisible = 5;
              const hasMore = categorySkills.length > maxVisible + 1;
              const visibleSkills = isExpanded || !hasMore
                ? categorySkills
                : categorySkills.slice(0, maxVisible);
              const hiddenCount = categorySkills.length - maxVisible;

              return (
                <div className="skill-category" key={category}>
                  <h3 className="category-title">{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
                  <div className="skills-list">
                    {visibleSkills.map((skill, index) => (
                      <SkillCard key={skill.name} skill={skill} index={index} />
                    ))}
                    {hasMore && !isExpanded && (
                      <MoreButton
                        className={`skill-card skill-card-more${visibleSkills.length % 2 === 0 ? ' full-width' : ''}`}
                        onClick={() => toggleCategory(category)}
                        index={maxVisible}
                      >
                        <span className="skill-more-count">+{hiddenCount}</span>
                        <span className="skill-more-label">Voir plus</span>
                      </MoreButton>
                    )}
                    {hasMore && isExpanded && (
                      <motion.div
                        className={`skill-card skill-card-more${categorySkills.length % 2 === 0 ? ' full-width' : ''}`}
                        onClick={() => toggleCategory(category)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <span className="skill-more-label">Réduire</span>
                      </motion.div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/**
          <div className="skills-image">
            <img
              src={require("../assets/images/pelemele.jpg")}
              alt="Montage de code"
              className="code-montage"
            />
          </div>
           */}
           
        </div>
      </div>
    </section>
  );
};

export default Skills;